// src/lib/permissions.js
// Canonical permission predicates — Issue #4/#58 consolidation, Session 62c.
// Composes the role booleans AuthContext already derives from profile.role.
// Does not re-derive from profile.role itself — that stays AuthContext's job,
// so there's exactly one place role strings get parsed, not two drifting apart.

// ── Canonical base predicates ──
export function isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive }) {
  return !!(isAdmin || isManager || isExecutive);
}

export function isAdminOrExecutive({ isAdmin, isExecutive }) {
  return !!(isAdmin || isExecutive);
}

export function isStaffWithPurchasing({ isAdmin, isManager, isExecutive, isPurchasing }) {
  return !!(isAdmin || isManager || isExecutive || isPurchasing);
}

export function isAdminOrManager({ isAdmin, isManager }) {
  return !!(isAdmin || isManager);
}

export function isBranchScopedManager({ isAdmin, isManager, isExecutive }) {
  return !!(isManager && !isAdmin && !isExecutive);
}

// #478, Speak Up OOB-6. THE ONE DEFINITION OF "BRANCH MANAGER" IN THIS APP.
//
// Colby's instruction at this session's open: "all that matters is that stede
// and speakup both know what a branch manager is." Two callers below already
// asked this question and each answered it inline, which is exactly what
// locked rule 8 forbids — a boolean combination written twice is a definition
// that can drift in one copy. Speak Up would have made a third.
//
// It reads users.manager_scope, the stored fact Session 78n4 (#272/#396)
// added. Before that, "runs the branch" was inferred from the Department field
// being empty, which is also what "nobody finished filling this in" looks
// like — the same state on screen, and the wider authority won.
//
// NOT THE SAME QUESTION AS isBranchScopedManager() ABOVE, which asks "is this
// person confined to one branch" and knows nothing about departments. An
// Install manager passes that and is not a branch manager. Both are correct;
// they are different questions and the names are close enough to be worth
// saying so here.
//
// AN ADMIN OR EXECUTIVE IS NOT A BRANCH MANAGER. They frequently hold more
// authority, which is a separate matter for each caller to decide — every one
// of them below grants admin and executive first, on its own terms, before
// reaching this line. Baking that in here would make the predicate mean "has
// branch-manager powers", and the reason #478 exists is that an admin was
// able to act AS a branch manager rather than merely outrank one.
//
// Nothing here names a branch or a person. Setting a manager's Department to
// "All departments (branch manager)" makes this true for them on save, with
// nothing to deploy — which is what makes Chance's Austin profile a data
// change rather than a code change.
export function isBranchManager({ isManager, profile }) {
  return !!(isManager && profile?.manager_scope === 'branch' && profile?.branch_id != null);
}

// #472, out-of-band Speak Up session, Sep 4 2026. Gate decision D2.
//
// NARROWED: this read `isAdmin || profile?.suggestion_admin` until now, so
// every administrator could open every anonymous submission. Colby's
// instruction was that only the person actually assigned to Speak Up reads
// them, and he is the administrator whose access this removes.
//
// THE APP HALF OF THIS IS THE DOOR HANDLE, NOT THE LOCK. The real gate is
// the Postgres function is_suggestion_admin(), which every admin_* RPC calls
// before returning a row, and it carried the identical widened condition.
// Changing only this file would have hidden the menu while leaving the data
// reachable by anyone who called the RPC directly. Both narrowed together in
// db/migrations/20260904_speakup_access_narrowed_to_assigned_admin.sql.
//
// Deliberately NOT isAdminOrExecutive, and not widened to Joe LePine as the
// alternate recipient of gate decision D18a. The alternate is a per-record
// routing question -- one reporter choosing where one concern goes -- and it
// cannot be answered by a predicate that reads only `auth`. It ships with the
// rest of the gate, in the same shape as canApprove().
//
// On production this admits exactly one person: Angie LePine, who carries the
// flag as an executive rather than as an administrator. Verified live before
// the migration ran, because if she had been reaching the inbox any other way
// this change would have locked out the only person meant to have it.
export function isSuggestionAdmin({ profile }) {
  return !!profile?.suggestion_admin;
}

// Speak Up OOB-3, #473 gate decision D18a. The alternate is the person a
// reporter can route a concern to instead of the handler — "visible only to
// him; Angie is not told it exists." Like the handler, the FLAG grants this
// and never a role, which is #472's finding and is not re-litigated here.
export function isSpeakUpAlternate({ profile }) {
  return !!profile?.speakup_alternate;
}

// Who may open the Speak Up screen at all. EITHER flag gets you through the
// door; WHICH ROWS you then see is decided in the database by
// can_read_suggestion_row(), never here.
//
// This predicate exists because the app gate is the THIRD home of the routing
// rule, and OOB-1 proved that fixing fewer than all three moves a leak rather
// than closing it. Without it the alternate would hold database access and
// have no menu item to reach it with — access that exists and cannot be used,
// which is the same class of broken promise as a checkbox with no recipient.
export function canOpenSpeakUp(auth) {
  return isSuggestionAdmin(auth) || isSpeakUpAlternate(auth);
}

export function canActionPurchasingQueue({ isAdmin, isManager, isExecutive, isPurchasing, profile }) {
  return !!(isAdmin || isExecutive || isPurchasing || (isManager && profile?.purchasing_actions_enabled !== false));
}

// #289, Session 74z2 — segregation of duties. Two changes, and the first is the
// one that closes the hole.
//
// `isPurchasing` used to grant this unconditionally, so the per-user toggle
// applied only to managers and there was no way to take vendor-catalog access
// away from a purchasing user at all. That mattered because the same person
// could then create a vendor and reconcile invoices against it — the conflict
// SAP's own segregation literature names as one of its most critical, because
// it puts the whole fictitious-vendor cycle in one pair of hands.
//
// `=== true` rather than `!== false` is deliberate and is the second change.
// CLAUDE.md names the trap: `undefined?.field !== false` evaluates to TRUE, so
// a profile that has not loaded yet used to read as *enabled*. All three toggle
// columns are `bool NOT NULL DEFAULT true` (verified against SCHEMA_REFERENCE
// and the live database), so this costs nothing for a loaded profile and fails
// closed for one that is missing. On a money-path capability that is the only
// correct direction to fail.
//
// Note this does not take anything away from an EXISTING purchasing user —
// their column is already true. It makes the capability removable, which it was
// not. Jennifer and Bridgette get their opposite toggles set by hand in User
// Management; only the company default for NEW accounts is flipped in the
// migration.
export function canActionVendorCatalog({ isAdmin, isManager, isExecutive, isPurchasing, profile }) {
  return !!(isAdmin || isExecutive || ((isManager || isPurchasing) && profile?.vendor_catalog_actions_enabled === true));
}

export function canReconcileInvoice({ isAdmin, isManager, isExecutive, isPurchasing, profile }) {
  return !!(isAdmin || isExecutive || ((isManager || isPurchasing) && profile?.invoice_reconciliation_enabled === true));
}

// #504, Session 79.1 — who may attach a packing slip to a receive that has none.
//
// WHY THIS EXISTS. A packing slip could only ever be attached at the instant of
// receiving. If the driver had not handed it over, or the photo failed on shop
// wifi (#497), or somebody simply forgot, that receipt could never carry one —
// Stede had no way to add a document to anything after the fact, checked across
// all six upload points in the app at Session 79.1.
//
// THREE AUDIENCES, and each is here for a stated reason rather than by analogy:
//
//   * THE PERSON WHO RECORDED THE RECEIVE. They are standing at the pallet
//     holding the paper. Anyone else has to be given it first, so leaving them
//     out is what makes the feature useless in the case it exists for.
//   * A MANAGER AT THAT BRANCH. Already accountable for the count, already sees
//     every transaction on the branch's ledger. Scoped to the branch on purpose:
//     a Houston manager has no business filing Dallas's paperwork, which is the
//     same reasoning #500's audit routing follows.
//   * ACCOUNTS PAYABLE. Colby's addition, Session 79.1: "accounts payable as
//     they are eventually handed all packing slips." That is the real workflow —
//     the slip travels to the person matching it against the invoice, so she is
//     the one most likely to be holding a slip whose receipt has none. Keyed on
//     canReconcileInvoice rather than on a role, because AP is a CAPABILITY in
//     this app (invoice_reconciliation_enabled), not a job title — the same
//     lesson #488 taught when accounts payable could not see the cost she sets.
//
// NOT scoped to the AP person's own branch, deliberately: invoicing is run
// centrally, the same way purchasing spans branches from day one.
//
// TAKES THE RECORD, like canApprove(), because two of the three tests are about
// THIS transaction — who wrote it and where. A capability-only predicate cannot
// answer "did you receive this?".
//
// ADD-ONLY IS ENFORCED BY THE CALLER, not here. A slip that already exists is
// evidence, and replacing evidence needs an audit trail Stede does not yet have.
// This predicate answers "may you attach one", and the screen only offers the
// control when the field is empty.
export function canAttachPackingSlip(auth, tx) {
  if (!tx) return false;
  const { isAdmin, isManager, isExecutive, profile } = auth || {};
  if (!profile?.id) return false;
  if (String(tx.user_id) === String(profile.id)) return true;
  if (canReconcileInvoice(auth)) return true;
  if (isAdmin || isExecutive) return true;
  // A branch manager, and only for their own branch.
  if (isManager && tx.branch_id != null && String(tx.branch_id) === String(profile.branch_id)) return true;
  return false;
}

// #288, Session 74z3 — the short-close gates. Two predicates, not one shared
// "can short-close" capability, and that is the load-bearing decision rather
// than a naming preference.
//
// The two flags are set by two different people from two different facts.
// Jennifer is the one who phoned the vendor and heard the balance is not
// coming; Bridgette is the one holding the invoice who knows it is the last
// one. So receipt-close follows the buyer's capability and invoice-close
// follows AP's, and Jennifer must be able to close for receiving while being
// unable to reconcile at all — which is exactly the separation #289 shipped.
//
// SAP divides them the same way: the delivery-completed indicator is set on the
// purchase order by the buyer, the final-invoice indicator during invoice
// verification by AP. Two indicators, two documents, two people.
//
// These delegate rather than re-deriving, per locked rule 8, and they exist as
// their own names for the reason canSeeInvoicingNav gives: the question "may
// this person declare receiving finished" is worth being able to change without
// hunting through call sites for a re-used predicate.
//
// canActionPurchasingQueue grants isPurchasing unconditionally, so Jennifer
// keeps the receipt flag with both her toggles off. Verified by reading the
// predicate, not assumed.
export function canCloseLineForReceiving(auth) { return canActionPurchasingQueue(auth); }
export function canCloseLineForBilling(auth) { return canReconcileInvoice(auth); }

// #289 + #295, Session 74z2. Seeing a vendor's phone number is not maintaining
// vendor master data, and the two must not share a predicate.
//
// Found while drawing #295's expandable order section: that section carries the
// vendor contact card, the card renders behind canContactVendor, and
// canContactVendor delegated to canActionVendorCatalog alone. So turning
// Bridgette's vendor-catalog capability off — which is the entire point of #289
// — would have removed the vendor's phone and email from the invoicing detail,
// the exact field #295 exists to deliver. Two individually correct predicates,
// a defect only in the combination: the same shape #289 describes about itself.
//
// Querying an invoice with the vendor IS accounts payable's job. SAP's rule is
// create-vendor plus pay-invoice — fabricating a payee, not phoning one — and
// SAP separates vendor DISPLAY from vendor MAINTAIN for exactly this reason.
//
// Decided with Colby at 74z2's gate. He first proposed keeping the card and
// removing its Call and Email buttons; that was not taken, because both are
// plain tel: and mailto: links and the number and address render as text beside
// them either way — it removes a tap, not a capability. What it would remove is
// tap-to-call on the priority surface.
//
// The line that DOES matter is communicating versus transacting: #257 wants to
// turn that same Email button into a draft-the-vendor-order action, which is a
// purchasing act. When #257 lands it needs its own gate rather than riding this
// one.
export function canSeeVendorContact(auth) {
  return canActionVendorCatalog(auth) || canReconcileInvoice(auth);
}

export function canApprovePurchaseThreshold({ isAdmin, isExecutive }) {
  return isAdminOrExecutive({ isAdmin, isExecutive });
}

// Master-data governance — Session 74u, #262 gap (3) and #198's governance half.
// Company-wide taxonomies (inventory_categories, departments) are executive-level
// master data: one wrong rename reshapes every rollup that groups by them.
// Stations are deliberately looser — see canGovernStations below.
export function canGovernTaxonomy(auth) { return isAdminOrExecutive(auth); }

// Stations/trucks are branch-local, so a branch's own manager governs its list.
// #198's stated reason: a truck rename does not carry the cross-branch reporting
// risk a department rename does. Takes the branch being edited, because no
// predicate in this file can answer a per-record question from `auth` alone —
// the same gap that produced canApprove.js.
//
// Admin and executive govern every branch. A manager governs exactly one — their
// own — and only if they are a BRANCH manager rather than a department manager.
// A station list is branch-level master data, the same way SAP maintains work
// centres at plant level rather than by a departmental supervisor; an Install
// manager renaming a Production truck is the cross-boundary edit that scoping
// exists to prevent.
//
// FIXED at Session 78n4, #272/#396. The weakness recorded here from 74u is gone
// and the original wording is kept below because it explains why the line changed.
//
// Superseded text: "Known weakness, deliberately accepted at 74u and hosted at the
// Sep 22-26 JWT/RLS sprint: 'branch manager' has no explicit representation
// anywhere in this codebase. It is inferred from department being empty, which is
// also what a manager whose department was simply never filled in looks like. This
// predicate is strictly tighter than what it replaced, but it does harden that
// convention, and the convention is the thing that needs fixing."
//
// It now reads a stored fact, `manager_scope`, instead of inferring one from an
// absence. "Deliberately branch-wide" and "nobody finished filling this in" were
// the same state on screen, and the wider authority was the one that won.
//
// The set this admits is unchanged for every existing manager — the migration
// backfilled `manager_scope` from exactly the condition this line used to test —
// so nobody gains or loses station governance today. What changes is that the
// answer is now recorded rather than guessed, which is the whole of #272.
//
// OOB-6: the manager_scope test moved into isBranchManager() and is called
// rather than repeated. Same people, same answer — see that predicate's note.
export function canGovernStations({ isAdmin, isManager, isExecutive, profile }, branchId) {
  if (isAdmin || isExecutive) return true;
  if (branchId == null) return false;
  if (!isBranchManager({ isManager, profile })) return false;
  return String(profile?.branch_id) === String(branchId);
}

// Who may turn another person's material-request approval switch on or off.
// #396, Session 78n4. Colby's explicit instruction at this session's open:
// "make sure branch managers can change the approval setting for their
// department managers."
//
// This is deliberately NOT `canManagePermissionToggles` (admin or executive),
// which still governs the purchasing, vendor-catalog and invoice-reconciliation
// switches. Those three are company-level money-path capabilities. This one is
// the branch manager delegating a piece of their own authority downward, and
// making them file a ticket to do it would mean the routing never gets used.
//
// Takes the person being edited, because like canApprove() this is a per-record
// question and no predicate reading `auth` alone can answer it.
//
// Three limits, each one closing a way this could widen authority sideways:
//
//   1. A branch manager may only act on someone at their OWN branch.
//   2. Only on a DEPARTMENT manager. Not on another branch manager, not on an
//      admin, executive, purchasing user or team member. A branch manager cannot
//      promote a peer, and cannot reach anyone whose authority is company-wide.
//   3. Not on themselves. A branch manager's own approval is structural and not
//      switchable — canApprove() grants it from `manager_scope` without ever
//      reading the switch — so a self-edit could only ever be a no-op that looked
//      like it had done something.
//
// OOB-6: the manager_scope test moved into isBranchManager() and is called
// rather than repeated. Same people, same answer — see that predicate's note.
export function canGrantRequestApproval({ isAdmin, isManager, isExecutive, profile }, targetUser) {
  if (isAdmin || isExecutive) return true;
  if (!targetUser) return false;
  if (!isBranchManager({ isManager, profile })) return false;
  if (targetUser.id === profile?.id) return false;
  if (targetUser.role !== 'manager' || targetUser.manager_scope !== 'department') return false;
  return String(targetUser.branch_id) === String(profile?.branch_id);
}

// #811, Session 296 — who may change this person from User Management. THE
// SCREEN'S COPY OF #798's GUARD, guard_user_row_changes(), which has decided
// every change to a person's record on production since Session 294
// (db/migrations/20261021_people_record_guard_798.sql). Before this, the page
// offered Edit and Deactivate beside everyone to every manager and to
// Purchasing, and the database refused the press — the right answer at the
// wrong moment.
//
// The guard's rule, as a person meets it:
//   - an admin or an executive may change anyone;
//   - a manager may change someone BELOW them at their OWN branch — a team
//     member, and for a branch manager also a department manager (#396);
//   - nobody else changes anybody else: not Purchasing, not a team member.
//
// A person's own record is not this page's business: the guard lets a manager
// change only their own My profile fields, so Edit on their own row could only
// ever refuse. Takes the person being edited, because like canApprove() this is
// a per-record question and no predicate reading `auth` alone can answer it.
//
// If the guard changes, this changes with it — permissions.test.js holds the
// two to the same cases, and Session 296's screen check reads them as a branch
// manager against the test database.
export function canChangePerson({ isAdmin, isManager, isExecutive, profile }, target) {
  if (!target) return false;
  if (isAdmin || isExecutive) return true;
  if (!isBranchScopedManager({ isAdmin, isManager, isExecutive })) return false;
  if (profile?.branch_id == null || target.branch_id == null) return false;
  if (String(target.id) === String(profile?.id)) return false;
  if (String(target.branch_id) !== String(profile.branch_id)) return false;
  if (target.role === 'team_member') return true;
  return isBranchManager({ isManager, profile })
    && target.role === 'manager' && target.manager_scope === 'department';
}

// #811 — Deactivate and Reactivate: canChangePerson() less one's own row, for
// everybody, admins included (the approved drawing, R2). Deactivating yourself
// locks you out with nobody but another admin to let you back in; Remove
// already refuses a self-removal on the screen and on the server, and this is
// the same line for the reversible action.
export function canSetPersonActive(auth, target) {
  if (!target || String(target.id) === String(auth?.profile?.id)) return false;
  return canChangePerson(auth, target);
}

// ── Inventory ──
export function canSwitchBranch(auth) { return isAdminManagerOrExecutive(auth); }

// #369, Session 78g. This was `isAdminManagerOrExecutive`, which locked out the
// two roles that are not managers — and on Sep 1 the people walking the Dallas
// racks with a clipboard are `team_member`. They could not enter a single
// number on the opening count.
//
// Written as an explicit five-role set rather than `return true` on purpose,
// and the callers now pass the whole auth object including `profile` to feed
// it. Two reasons that are worth the extra plumbing:
//
// 1. SAP's split is that a threshold governs POSTING, not ENTRY. Stede had
//    that inverted — it blocked entry outright and then alerted on everything
//    that got past the block. The threshold model is #363, Oct 1, and it lands
//    on this line rather than by re-plumbing three call sites.
// 2. `return true` would carry no information, so the next session could not
//    tell a deliberate grant from a predicate nobody had gotten to yet.
//
// `purchasing` is IN, by Colby's call at 78g. Reversible by deleting one term.
// The audit trail is untouched either way: `inventory_transactions` records the
// signed delta, reason, user, timestamp and note on every adjustment.
export function canAdjustInventory({ isAdmin, isManager, isExecutive, isTeamMember, isPurchasing }) {
  return !!(isAdmin || isManager || isExecutive || isTeamMember || isPurchasing);
}
export function canManageRequests(auth) { return isAdminManagerOrExecutive(auth); }
// #488, Session 78w. WIDENED to the person who settles what something cost.
//
// FOUND BY BUILDING THE ACCEPTANCE PACKS, not by reading this file. The packs
// name the capability every line depends on and a test evaluates it against
// these predicates for the persona holding that pack — and accounts payable's
// cost-basis lines failed. She enters the invoice that DECIDES what an item
// cost and then could not see that number on the item page or in the Ledger.
// The 78o acceptance delta had been telling testers to run its whole cost-basis
// section "as accounts payable", so that section was untestable by the role it
// named, and nobody had noticed because nobody had run it.
//
// Colby's ruling: only accounts payable, not purchasing generally. So this is
// keyed on the CAPABILITY rather than the role — canReconcileInvoice already
// means "may settle a bill", and reading the cost that bill produces belongs
// with settling it. Bridgette sees cost because she reconciles; Jennifer does
// not, and that is #289's separation holding rather than an accident: the
// person who creates a vendor and negotiates with them still does not see what
// the company's stock is carried at.
//
// TEAM MEMBERS ARE UNAFFECTED and the old warning still stands. Session 78l
// granted them read-only Catalog access on the premise that no cost figure
// renders there (#77), and that premise survives only because this predicate
// excludes them. canReconcileInvoice is false for team_member on both terms —
// the role test and the capability flag — so widening through it cannot reach
// them. Do not widen this predicate any other way without reopening #77.
//
// LOCKED RULE 8 APPLIES TO EVERY CALLER, and it is not theoretical here. This
// now reads `profile` and `isPurchasing` through canReconcileInvoice. A call
// site passing only { isAdmin, isManager, isExecutive } hands it an undefined
// profile, `undefined?.invoice_reconciliation_enabled === true` is false, and
// accounts payable silently gets nothing — the same shape that nearly took the
// catalog from every branch manager at 78t. Both call sites were swept in this
// same commit: InventoryItemDetail.js and InventoryLedgerPage.js.
export function canSeeInventoryCost(auth) {
  return isAdminManagerOrExecutive(auth) || canReconcileInvoice(auth);
}
// #448, Session 78o — catalog master data. ONE rule for every door that creates
// or changes a catalog item, because until now there were two and they admitted
// different people.
//
// What was wrong: `canAddInventoryItem`/`canEditInventoryItem` were
// admin-or-manager and `canPromoteToCatalog` was admin-or-executive. NEITHER SET
// CONTAINED THE OTHER. A manager refused the promote button could create the
// identical item with "+ Add item" on the next screen, and an executive could
// post a catalog entry by promotion but not edit the name of the thing they had
// just posted. `promote_off_catalog_item()` runs `INSERT INTO inventory_items`,
// so the two paths were never different acts — only different doorways.
//
// Colby's ruling at 78o: "the permission is granted by at least branch manager
// level or higher." That is this set, and it resolves both halves at once —
// managers gain promote, executives gain add and edit.
//
// WHAT THIS DELIBERATELY GIVES UP, recorded because the losing argument was a
// real one. canPromoteToCatalog's own comment narrowed it on the reasoning that
// "only admin/executive post a new catalog entry", which is SAP MDG's
// requester-proposes/approver-posts split. That split is abandoned here rather
// than half-enforced: it was never true, because the direct route was open to
// managers the whole time, and a control that one door enforces and another
// ignores is worse than no control — it reads as governance and is not.
//
// The real version of that governance is #448's stages 2 and 3: an audit log on
// inventory_items (there is none today — `vendor_item_audit_log` and
// `company_settings_audit_log` exist and are the pattern to copy), and then an
// approval step for which the off-catalog review queue is already the skeleton.
// Colby also raised moving EDIT to executive-only; deliberately not done here,
// because it locks a branch out of fixing its own typo, and the audit log should
// say whether that is a real risk before a capability is taken away.
// NARROWED AT SESSION 78t (Sep 5, 2026) on Colby's ruling, and this is as much
// a CORRECTION of the line above as a new restriction.
//
// 78o's ruling was "the permission is granted by at least branch manager level
// or higher." What it BUILT was isAdminManagerOrExecutive(), which is blind to
// what KIND of manager somebody is and so admitted DEPARTMENT managers too. At
// the time "branch manager" was not a fact Stede stored, so the sentence could
// not be written exactly. Session 78n4 added users.manager_scope and OOB-6 gave
// it one definition — isBranchManager() — so it can be now.
//
// Counted on production before the change, per OOB-6's rule that a permission
// change which narrows an audience must count that audience first: 14 people
// governed the catalog, 8 do after, and the 6 who lose it are every department
// manager. No branch manager is stranded — all three carry a branch_id.
//
// THE SWITCH. A branch manager holds this unless somebody turns it off, which
// is the opt-OUT shape (`!== false`) that purchasing_actions_enabled uses, not
// the opt-IN shape (`=== true`) of vendor_catalog_actions_enabled. Colby asked
// for exactly that: on for branch managers, switchable off to leave admins and
// executives. Through September Dallas is one branch and its branch manager is
// the catalog's operational owner; in January, four branch managers adding
// items independently is where duplicates arrive, and switching some off is
// then a data change with nothing to deploy.
//
// LOCKED RULE 8 APPLIES TO EVERY CALLER OF THIS, and it is not theoretical.
// This predicate now reads `profile`, so a call site passing only
// { isAdmin, isManager, isExecutive } hands isBranchManager() an undefined
// profile, `undefined?.manager_scope === 'branch'` is false, and EVERY BRANCH
// MANAGER LOSES ADD, EDIT AND PROMOTE. Three call sites did exactly that and
// were fixed in the same commit: InventoryCatalogPage.js, InventoryItemDetail.js
// and OffCatalogReviewPage.js. Swept at 78t; all callers pass profile.
export function canGovernCatalogItem(auth) {
  const { isAdmin, isExecutive, profile } = auth || {};
  if (isAdmin || isExecutive) return true;
  return !!(isBranchManager(auth || {}) && profile?.catalog_actions_enabled !== false);
}

export function canEditInventoryItem(auth) { return canGovernCatalogItem(auth); }
export function canAddInventoryItem(auth) { return canGovernCatalogItem(auth); }
// #370, Session 78j — widened from isAdminOrExecutive to isStaffWithPurchasing
// on the workbench gate's decision W8, with purchasing included by Colby's
// ruling at 78f (callout E, overturned).
//
// The argument is that desktop emphasises holistic multi-location visibility
// while mobile stays location-specific, and that branch managers are the people
// who fulfil an inter-branch transfer, so the all-branches table is their
// working surface rather than a convenience. This is an efficiency change, not
// an enablement one: canSwitchBranch is already isAdminManagerOrExecutive, so a
// manager could always read any branch's shelf one at a time. What nobody could
// do is see four at once.
//
// The one thing to know before calling this: InventoryLedgerPage.js also
// consumed it, and it asks canSeeLedgerAllBranches() below instead. Widening a
// shared predicate silently widens every consumer, and the Ledger's export path
// pulls every row it can see. See the comment at that call site.
export function canSeeAllBranches(auth) { return isStaffWithPurchasing(auth); }
// #153, Session 297 (Colby's R1, Oct 31, 2026): Purchasing reads every branch's
// stock history; their home branch only says where they sit. Branch managers keep
// the Ledger at their own branch, which is why this is not canSeeAllBranches(). The
// database's rule on the stock history names the same three people
// (20261031_people_lookups_68_153.sql). Cost is canSeeInventoryCost()'s to decide.
export function canSeeLedgerAllBranches({ isAdmin, isExecutive, isPurchasing }) {
  return !!(isAdmin || isExecutive || isPurchasing);
}
export function canSeeBranchComparison(auth) { return isAdminOrExecutive(auth); }
export function canReviewOffCatalog(auth) { return isStaffWithPurchasing(auth); }

// Deliberately narrower than canReviewOffCatalog, which stays staff-plus-purchasing.
// Working the queue and creating permanent company-wide master data are two
// different acts: purchasing and branch managers keep full visibility and can
// still dismiss, but only admin/executive post a new catalog entry. Interim
// control until the promotion approval workflow ships (Sep 1-2); it is the
// requester-proposes/approver-posts split SAP MDG uses, minus the pending state.
// Was re-derived inline as isStaffWithPurchasing() at two call sites before 74u.
// #448, Session 78o — now the same rule as the direct route. See
// canGovernCatalogItem above for what changed and what it gives up.
export function canPromoteToCatalog(auth) { return canGovernCatalogItem(auth); }

// ── Vendor ──
export function canFullAccessVendor(auth) {
  return canActionVendorCatalog(auth);
}
export function canSeeVendorPackPrice(auth) { return canFullAccessVendor(auth); }
export function canContactVendor(auth, settings) {
  return canSeeVendorContact(auth) || !!(auth.isTeamMember && settings?.team_members_can_contact_vendors);
}
export function canSeeVendorPricing(auth) { return canActionVendorCatalog(auth); }
export function canTogglePreferredVendor(auth) { return canActionVendorCatalog(auth); }
export function canLockVendor(auth) { return canActionVendorCatalog(auth); }
export function canEditVendor(auth) { return canActionVendorCatalog(auth); }
export function canAddVendor(auth) { return canActionVendorCatalog(auth); }
export function canEditVendorPrice(auth) { return canActionVendorCatalog(auth); }
export function canDeactivateVendor(auth) { return canActionVendorCatalog(auth); }
export function canReviewVendor(auth) { return isAdminOrExecutive(auth); }

// #194, Session 78n10. Bulk CSV out and bulk CSV in.
//
// Both answer canActionVendorCatalog today, and both are named anyway — the
// house pattern this block already follows, and the same reason canAddUnit
// exists below rather than reusing the predicate it delegates to.
//
// The export carries phone and email for every vendor, so it sits behind the
// same gate that decides whether a person may see vendor contact details at
// all. Nothing new is disclosed by putting them in a file.
export function canExportVendorCatalog(auth) { return canActionVendorCatalog(auth); }

// The import was the one worth arguing about, because it writes master data in
// bulk and canGovernTaxonomy is admin/executive for exactly that reason.
// It is NOT restricted to admin/executive, deliberately: Jennifer can already
// create and edit vendors one at a time through canAddVendor and canEditVendor,
// so an admin-only import would not be a safety boundary — it would let her do
// a hundred times what she may already do once, only slower. The real guard is
// the preview step, which shows every create, update and rejection before
// anything is written, and that protects an admin exactly as much as it
// protects her.
export function canImportVendorCatalog(auth) { return canActionVendorCatalog(auth); }

// ── Units of measure ──
// #398, Session 78n2. Who may ADD a unit to the governed list without leaving
// the screen they are on. Named rather than reusing canActionVendorCatalog at
// the call sites, because the intent is different even though the answer is the
// same today — this is the house pattern, the same reason canEditVendorPrice
// exists beside it.
//
// Colby's requirement, in his words: Jennifer cannot sit in the middle of an
// order waiting for someone else to enter a new unit. So purchasing adds inline.
//
// ADD ONLY. There is deliberately no canDeleteUnit and there never should be:
// the database refuses a delete on a unit in use (ON DELETE RESTRICT on all
// three foreign keys), and RETIRING one is a decision about the company's
// vocabulary rather than an unblock — that stays with the executive in Company
// Settings. Adding cannot break anything; removing can, and removing solves
// nothing Jennifer actually has. NetSuite draws the line in the same place:
// once a unit is in use you may only add to the list.
export function canAddUnit(auth) { return canActionVendorCatalog(auth); }

// ── Cross-cutting ──

// #308, Session 74z6. The executive dashboard.
//
// Same role set as the purchasing nav today, and kept on its own predicate for
// the reason canReviewOffCatalog gives: these two answer different questions and
// changing one must not silently move the other. Purchasing is included
// deliberately — the KPIs on this surface are Jennifer's and Bridgette's own
// numbers, and a dashboard that hides them from the people who generate them
// would be an odd definition of transparency.
//
// Team members are excluded: company spend and vendor concentration are not
// shop-floor information.
export function canSeeInsights(auth) { return isStaffWithPurchasing(auth); }

export function canAccessCompanySettings(auth) { return isAdminOrExecutive(auth); }

// #298, Session 78n9.3. Who may reach Integrations, and connect or configure
// QuickBooks.
//
// Deliberately the SAME set as canAccessCompanySettings rather than a stricter
// one. Colby's instruction at this session's open: Angie is an executive and is
// the person most likely to authorise Stede against D&R's real books. An
// integration only an admin could connect, reached from an area an executive
// can already open, would be stricter than its own doorway for no reason
// anyone could state.
//
// It is its own predicate rather than a call to canAccessCompanySettings at the
// call site, for the reason canSeeInvoicingNav gives: reaching a surface and
// configuring it are separate questions, and the day Integrations grows a
// second tenant -- label printing and outbound email are both waiting -- this
// is where that divergence gets expressed.
//
// Purchasing is excluded HERE and allowed at /quickbooks-refdata, which is not
// an inconsistency: Bridgette needs the supplier list to send a bill, and does
// not need the authority to grant Stede write access to the books.
export function canManageIntegrations(auth) { return isAdminOrExecutive(auth); }
export function canAccessSpeakUpAdmin(auth) { return canOpenSpeakUp(auth); }

// Speak Up OOB-4. THREE SURFACES, THREE DIFFERENT READING RULES, so three
// predicates rather than one — a manager is not a cut-down handler and an
// executive is not a cut-down manager. Each function behind these enforces
// its own rule server-side; these only decide whether a menu item is drawn.
//
//   handler  submissions, scoped by can_read_suggestion_row()   (D2, D18a)
//   manager  their branch's ideas as written, plus SUMMARIES shared
//            with them — never a concern, never an original      (D4, D9, D11)
//   exec     counts, with no way to open anything at all         (D19)
//
// OOB-6 CHECKED THIS AND DELIBERATELY LEFT IT WIDE. #478 narrows who reads
// IDEAS, and the obvious move was to narrow the page with it. That would have
// been wrong: admin_list_share_recipients() offers the handler EVERY active
// manager and executive, so a Production manager can have a concern summary
// shared with them personally — and locking this predicate would have taken
// away the only screen that shows it, silently, after she had chosen to send
// it. The page stays; the ideas list inside it is what narrows.
export function canSeeSpeakUpManager({ isManager, isExecutive, isAdmin }) {
  return !!(isManager || isExecutive || isAdmin);
}

// #478, OOB-6. Who may READ the ideas board.
//
// Colby's ruling: branch managers see their own branch; executives and admins
// see every branch, explicitly. A DEPARTMENT MANAGER SEES NOTHING — the idea
// form promises only "your branch manager will read this", so a departmental
// manager was never in the audience that sentence describes. Recorded as a
// decision rather than left to be noticed: it is reversible if D&R's
// department managers turn out to expect access.
//
// The database says the same thing in manager_list_ideas(), which raises
// access_denied rather than returning an empty list. This only decides whether
// the tab is drawn, so the screen does not ask a question it will be refused.
export function canReadSpeakUpIdeas({ isManager, isExecutive, isAdmin, profile }) {
  if (isAdmin || isExecutive) return true;
  return isBranchManager({ isManager, profile });
}

// #478, OOB-6. Who may ANSWER an idea, for the branch that idea came from.
//
// Per-record, so it takes the branch — the same reason canApprove.js exists
// and no predicate reading auth alone can stand in for it.
//
// Executives and admins are excluded ON PURPOSE and this is the whole of #478:
// the reply arrives signed "manager", the reporter reads it as the person the
// form named, so anyone else sending it makes the form a lie. Colby: "if we
// tell a user on their submission that your branch manager will read it i feel
// like it implys your branch manger will also be responding to it."
//
// The Speak Up handler still answers any idea from any branch, from her own
// inbox, and the reporter sees that one labelled "Ownership" — a different
// voice rather than a borrowed one.
//
// THIS ONLY HIDES A BUTTON. manager_reply_idea() enforces the same rule in the
// database, so a caller holding the submission id cannot go around the screen.
export function canReplyToSpeakUpIdea({ isManager, profile }, branchId) {
  if (branchId == null) return false;
  if (!isBranchManager({ isManager, profile })) return false;
  return String(profile?.branch_id) === String(branchId);
}

// #479, OOB-6. Who may print or download the wall flyers.
//
// A flyer is a public web address, a QR code, the D&R mark and a branch name.
// There is nothing on it to protect — it discloses nothing that a wall in a
// shop does not disclose by holding it.
//
// It lived inside the Speak Up inbox, which #472 narrowed to the assigned
// handler alone. That narrowing was right; the defect is that a harmless
// control sat inside a restricted surface and inherited a rule written for
// something else, so the only person who could print the posters was the one
// person whose job is reading what comes back. Gate decision D8 says assigning
// is not reading. The inverse needs saying too: PRINTING A POSTER IS NOT
// READING REPORTS.
//
// Branch managers are included because they are the people who actually put
// things on walls at their own site. Which BRANCHES a caller gets is decided
// at the call site, not here — a branch manager gets theirs, everyone else
// gets all four.
export function canPrintSpeakUpFlyers({ isManager, isExecutive, isAdmin, profile }) {
  if (isAdmin || isExecutive) return true;
  return isBranchManager({ isManager, profile });
}
export function canSeeSpeakUpLedger({ isExecutive, isAdmin }) {
  return !!(isExecutive || isAdmin);
}
export function canSeeNotificationSuggestions(auth) { return canOpenSpeakUp(auth); }
export function canSeePurchasingNav(auth) { return isStaffWithPurchasing(auth); }

// #284, Session 74z. The Invoicing page's nav entry. It delegates to the same
// capability that gates the action on the page, so the navigation cannot show a
// surface the user could not act on — which is the exact contradiction #284 was
// raised about, the other way round.
//
// Kept as its own predicate rather than calling canReconcileInvoice at the call
// site, for the reason #243 gave when it did the same for off-catalog review:
// seeing a surface and acting on it are separate questions, and #289 (74z2)
// changes what canReconcileInvoice returns. When that lands, decide here whether
// read-only visibility should outlive the ability to reconcile.
export function canSeeInvoicingNav(auth) { return canReconcileInvoice(auth); }
// #515, Session 79.8 — who may say what a made thing costs. Gate decision D32.
//
// ── What this controls, in plain words ──
//
// One number on an item: what it costs D&R to make one of them. Houston's
// stakes are the case — nobody ever BOUGHT a stake, so there is no purchase
// price to average, and without this figure everything Houston makes is worth
// nothing on paper. Dallas then receives a hundred of them and books in zero.
//
// ── Why it is a switch on a person and not a role ──
//
// The eighth of its kind, after invoice reconciliation, catalog actions and
// their six siblings. permissions.js already says the reason out loud a hundred
// lines above this: accounts payable is "a CAPABILITY in this app, not a job
// title". Costing what the shop makes is the same shape. The role list is
// locked at five and adding a sixth touches every permission check in the app.
//
// ── The one thing that makes this different from the catalog switch ──
//
// CHANGING THIS NUMBER REVALUES STOCK THAT IS ALREADY ON THE SHELF. Setting a
// stake from $3.70 to $4.10 with 312 on hand moves $124.80 without anybody
// touching the stock. That is why the screen states the figure in dollars
// before saving (gate callout F) and why this is a narrower grant than reading
// the cost, which any manager can do.
//
// `=== true` rather than `!== false`, and CLAUDE.md names the trap: a profile
// that has not loaded yet makes `undefined?.field !== false` evaluate to TRUE.
// The column is `bool NOT NULL DEFAULT true`, so this costs a loaded profile
// nothing and fails closed for a missing one. On anything that moves a money
// figure that is the only correct direction to fail.
//
// LOCKED RULE 8: THIS READS `profile`, SO EVERY CALL SITE MUST PASS IT.
// A caller handing over only { isAdmin, isManager, isExecutive } gets `false`
// for every manager, silently, and the set-cost panel simply never appears.
// Call sites at 79.8: RecordProductionSheet.js and InventoryItemDetail.js, both
// passing the whole auth object from useAuth().
// ── D64 — WHO SAYS HOW A LINE WAS FILLED ────────────────────────────────────
//
// #517, Session 79.10, and it exists because the acceptance run found the
// driver being asked.
//
// The gate is explicit: "The driver is never asked how a line was filled...
// Whether frames were cut this week or pulled off a rack is a FABRICATION FACT,
// and the driver loads a trailer. It defaults from what was approved and is
// answered, if it differs, by whoever stages the goods. The driver's screen
// asks one thing: how many."
//
// It matters beyond tidiness because the answer WRITES A PRODUCTION ROW. Saying
// "we made these" records a manufacturing run that either did or did not
// happen, and #515 exists because Houston's rack emptied in reality while Stede
// recorded a run nobody performed. That is not a question to put to somebody
// whose job that shift is to load a trailer.
//
// Manager and above, with no per-person switch: there is no existing toggle for
// a fabrication judgement, and inventing one here would be a settings row
// nobody has been told about. If Colby wants a narrower set later it belongs
// beside the other _enabled switches rather than inline at a call site.
export function canStateHowFilled({ isAdmin, isExecutive, isManager } = {}) {
  return !!(isAdmin || isExecutive || isManager);
}

export function canSetProductionCost(auth) {
  const { isAdmin, isExecutive, isManager, profile } = auth || {};
  if (isAdmin || isExecutive) return true;
  return !!(isManager && profile?.production_cost_actions_enabled === true);
}

// MAY THIS PERSON SAY "WE MADE THESE"? — #553 D5, Session 79.15e.
//
// Signing off a run is a shop-floor act: it moves a real number on a real rack
// and it is the thing that lets a truck be loaded. Colby's instruction was
// "fabrication or branch manager role", and THERE IS NO FABRICATION ROLE — the
// five are locked — so it is branch managers and above, plus a per-person
// switch, which is how a fabricator gets it. Six precedents on that column.
//
// HIS RULING AT 79.15e: branch manager is enough for September and he turns on
// individual fabricators himself from User Management. So the switch ships OFF
// for everyone, and that is safe ONLY because BRANCH managers qualify without
// it. A capability nobody can reach reads as finished — #515 — and 79.8 shipped
// a Record production button whose precondition no real row satisfied.
//
// ** #584, Session 79.21b — BUILT AS RULED AT LAST. ** This read `if (isManager)
// return true`, so every DEPARTMENT manager at the making branch — Install,
// Sales, 811, a manager for the Tools module only — could declare stakes made
// and move the rack count, while the paragraph above said "branch managers and
// above". Its test asked "a manager may" with no scope and passed either way.
// Now a branch manager signs off by role and anybody else — a department
// manager as well as a team member — needs the switch, because the fabrication
// manager is exactly the person the switch was for. **Before this reaches
// production at 79.22, Houston's fabrication manager needs the switch on**, or
// only Houston's branch manager can sign off (gate 553 D5's recorded hazard).
//
// ** THE GATE CONTRADICTED ITSELF ABOUT EXECUTIVES, AND COLBY RULED AT 79.15e:
// EXECUTIVES RIDE WITH ADMIN. **
//
// B4's permission row read admin Y, manager own-branch, team member
// own-branch-with-switch, EXECUTIVE N, purchasing N — while the same cell's
// prose said "branch manager and above", which includes an executive. Built to
// the matrix first and put to him rather than settled quietly, because this
// codebase's standing rule is the opposite of what that row said.
//
// His ruling restores the standing rule, and the cost of getting it wrong is
// asymmetric: an executive locked out of a shop-floor action reads as a broken
// screen to the two people who own the company, while an executive who CAN sign
// off still cannot sign off somebody else's branch — the database refuses that
// for everybody, admin and executive included.
//
// THIS ANSWERS THE ROLE QUESTION ONLY. Whether the person is AT the branch
// making the goods is a separate test, and the caller makes it — exactly as the
// ship button does, and for the same reason: `sign_off_production()` refuses
// anybody whose own branch is not the supplying branch, INCLUDING an admin. So
// admin and executive SEE every branch's make-work and still cannot sign off
// somebody else's, which is the pattern TransfersPage already documents.
// THE TWO FLOOR SWITCHES' BOXES, Session 79.22b.6.4.1 (#746 D4, with #553 D5
// and #584). "Can sign off production runs" and "Can record welds" change
// something for exactly two kinds of person: a team member, and a DEPARTMENT
// manager. Admins, executives and branch managers already qualify by role -
// canSignOffProduction() below and canRecordWelds() each grant them first -
// and purchasing never qualifies. So User Management draws both boxes for
// those two and nobody else: a box that changes nothing for the person in
// front of you reads as governance and is not. weldingAdmin746.test.js holds
// this to both rules for every role and manager type.
//
// `scope` is the form's own answer: 'branch', 'department', or empty while no
// department is chosen yet - which draws no box, as the inline rule this
// replaced did not either.
export function floorSwitchesApplyTo(role, scope) {
  return role === 'team_member' || (role === 'manager' && scope === 'department');
}

export function canSignOffProduction(auth) {
  const { isAdmin, isExecutive, isManager, isTeamMember, profile } = auth || {};
  if (isAdmin || isExecutive) return true;
  if (isBranchManager(auth || {})) return true;
  // Reads `profile`, so a call site that forgets to pass it gets `undefined?.x
  // === true` → false. That fails CLOSED here, which is the safe direction, but
  // it fails SILENTLY — locked rule 8, and the reason every call site is swept.
  return !!((isTeamMember || isManager) && profile?.production_signoff_enabled === true);
}

// MAY THIS PERSON CORRECT THIS RECORDED STEP? — #682 and #702, the corrections
// gate's 682 D3 and D15, Session 79.22b.6c.2.
//
// Correct sits on a step's own line in a transfer's history (682 D6): a sign-off,
// a truck's number at the branch it left, or a count's number at the branch that
// counted it — and, from 79.22b.6c.3, a run for the shelf on the item's Made
// line. Only the people this answers yes for see the button at all (gate F1).
//
// THE SCREEN'S HALF OF ONE RULE, AND THE DATABASE HOLDS THE OTHER. Every
// correction step calls assert_may_correct() (db/migrations/
// 20261005_correct_sign_off_and_shelf_run_702_761.sql, statement 7), and this
// is written from that function rather than from the gate's prose, so the two
// cannot disagree about who sees a button the database would refuse. This is
// the door handle; that is the lock (#623).
//
//   * AT THE STEP'S OWN BRANCH, for everybody — an admin or executive included.
//     An admin whose branch is Dallas cannot correct Houston's sign-off, the rule
//     sign-offs already follow (746 D22, and canSignOffProduction() above).
//   * A BRANCH LEAD — a branch manager, an admin or an executive — may correct
//     any step there. Every lead may sign off, so no switch is asked of them.
//   * ANYBODY ELSE only a step whose CURRENT number is theirs (682 D15). After a
//     branch manager corrects Owen's sign-off the number is the manager's, and
//     Owen no longer sees Correct on that line — the rule #751 already enforces
//     for every take-back. A department manager is "anybody else": Colby's
//     ruling on #763 (79.22b.6c, R2), own steps only.
//   * A SIGN-OFF OR A RUN FOR THE SHELF also needs the person still allowed to
//     sign off — canSignOffProduction(), which is sign_off_rule_allows() in the
//     database, clause for clause. A truck or a count asks nothing more.
//   * PURCHASING NEVER. It spans branches by design (Jennifer runs it from
//     Houston) and this gate grants it nothing.
//
// TAKES THE RECORD, like canAttachPackingSlip(): step = { kind, branchId,
// recordedBy }, where kind is 'sign_off', 'shelf_run', 'load' or 'count',
// branchId is the branch the step happened at (the making branch, the branch the
// truck left, the branch that counted), and recordedBy is whoever recorded the
// step's current number — the latest correction's corrector when it has one.
//
// READS `profile` for the branch and the person, so every call site passes the
// whole `auth` (locked rule 8). Without it the answer is false: closed, the safe
// direction, and silent, which is why the tests pin it.
export function canCorrectStep(auth, step) {
  if (!step) return false;
  const { isAdmin, isExecutive, isPurchasing, profile } = auth || {};
  if (!profile?.id || profile.branch_id == null) return false;
  if (isPurchasing || profile.role === 'purchasing') return false;
  if (step.branchId == null || String(step.branchId) !== String(profile.branch_id)) return false;
  if (isAdmin || isExecutive || isBranchManager(auth)) return true;
  if (step.recordedBy == null || String(step.recordedBy) !== String(profile.id)) return false;
  if (step.kind === 'sign_off' || step.kind === 'shelf_run') return canSignOffProduction(auth);
  return true;
}

// MAY THIS PERSON ANSWER "DID 4 GO ON THE TRUCK?" — 682 D7 and D13, the gate's B4
// row "Answer a truck extra, and see its card and count": at the sending branch,
// a branch lead, or whoever loaded that truck. Built at 79.22b.6c.2 beside its
// sibling; the card that uses it is 79.22b.6c.2b's.
//
// THE SAME RULE AS CORRECTING THE TRUCK, because Houston's Yes IS a correction
// of the truck: answer_load_extra() calls assert_may_correct() with the truck
// line's current recorder, so this delegates rather than re-deriving (locked
// rule 8). "Whoever loaded that truck" means whoever recorded the truck line's
// CURRENT number — the corrector, once it has been corrected (682 D15).
//
// extra = { fromBranchId, loadedBy }, as the gate names them.
export function canAnswerTruckExtra(auth, extra) {
  if (!extra) return false;
  return canCorrectStep(auth, { kind: 'load', branchId: extra.fromBranchId, recordedBy: extra.loadedBy });
}

// ── WELDING — #746, the Welding gate's B4, Session 79.22b.6.2d ───────────────
//
// SIX QUESTIONS THE WELDING PAGE ASKS, AND THE DATABASE ALREADY ANSWERS EACH.
// Every rule below is the screen's half of a database rule that refuses the
// wrong people on its own (db/migrations/20261010_welding_746.sql and
// 20261011_welding_reserve_746.sql). The screen's half decides which buttons
// are drawn; the database's half is the lock (#623). They must never disagree,
// so src/lib/weldingRules746.test.js holds all six to the test database's own
// yes or no for every role, manager type, switch and branch
// (src/data/weldingRuleAnswers746.json, recorded by
// db/migrations/20261011_welding_rule_answers_746.sql), and fails when either
// side changes alone.
//
// In plain terms (B4): anyone at Houston can look. The welders a manager has
// switched on, and Houston's branch manager, record, flag and count. Any
// Houston manager also counts, clears a flag and changes a reserve. Admins and
// executives look at any branch and clear a flag or change a reserve anywhere,
// but record and count only at their own branch. Purchasing looks and never
// records. Everyone else never sees the page.
//
// EVERY ONE READS `profile` (locked rule 8), so every call site passes the
// whole `auth` from useAuth(). Without it the answer is false: closed, the
// safe direction, and silent, which is why the tests pin it.

// Is the person's own branch this one? The database's assert_caller_at(): an
// empty branch on either side is never a match, for an admin as for anyone.
function isAtBranch(auth, branchId) {
  const own = auth?.profile?.branch_id;
  if (branchId == null || own == null) return false;
  return String(own) === String(branchId);
}

// MAY THIS PERSON SEE WELDING AT ALL? The menu item and the page (746 D5).
// Admins and executives always - they choose any welding branch with the
// branch chip (746 D23). Anybody else when their own branch welds, which
// auth.weldingBranchIds says: welding_branches(), read once at sign-in. The
// database's twin is welding_board()'s refusal - not at the branch, and not an
// admin or an executive - so purchasing at Houston sees it, read-only (B4).
//
// weldingBranchIds is null until the sign-in's read lands, and stays null while
// that read keeps failing - it tries again on its own (#801, AuthContext.js).
// Both answer false here: the item is hidden rather than offered and then
// refused.
export function canSeeWelding(auth) {
  const { isAdmin, isExecutive, profile, weldingBranchIds } = auth || {};
  if (isAdmin || isExecutive) return true;
  if (!Array.isArray(weldingBranchIds) || profile?.branch_id == null) return false;
  return weldingBranchIds.some(id => String(id) === String(profile.branch_id));
}

// MAY THIS PERSON RECORD WELDS, by role and switch (746 D4)? This is
// canSignOffProduction() with its own switch, because its database twin,
// weld_rule_allows(), is sign_off_rule_allows() character for character: an
// admin or an executive; a branch manager (isBranchManager() asks for a branch
// too, as the database's `p_branch IS NOT NULL` does); or a team member or any
// manager with "Can record welds" switched on. Purchasing never.
//
// THIS ANSWERS THE ROLE QUESTION ONLY. Whether the person is AT the branch is
// canRecordWeldsAt() below: the database refuses an admin at another branch
// too (746 D22).
export function canRecordWelds(auth) {
  const { isAdmin, isExecutive, isManager, isTeamMember, profile } = auth || {};
  if (isAdmin || isExecutive) return true;
  if (isBranchManager(auth || {})) return true;
  // `=== true`: a profile that has not loaded is no grant (locked rule 8).
  return !!((isTeamMember || isManager) && profile?.weld_record_enabled === true);
}

// ...AT THIS BRANCH? Record, its Undo, I'm on it, Help on this and Stop (B4).
// The database's assert_may_weld(): purchasing refused first, then anybody whose
// own branch is not this one - an admin or an executive included (746 D22) -
// then the welding rule.
export function canRecordWeldsAt(auth, branchId) {
  if (auth?.isPurchasing) return false;
  if (!isAtBranch(auth, branchId)) return false;
  return canRecordWelds(auth);
}

// THE BOTTOM BAR'S QUESTION (746 D24), Session 79.22b.6.3: does this person
// record welds at their OWN branch, and does that branch weld? Welding then goes
// on their bar by default (bottomBar.js, barForRole()). Composed from the rule
// above and the sign-in's list of welding branches - not a seventh rule, so it
// has no database twin to be held to.
export function canRecordWeldsHere(auth) {
  const own = auth?.profile?.branch_id;
  const ids = auth?.weldingBranchIds;
  if (own == null || !Array.isArray(ids) || !ids.some(id => String(id) === String(own))) return false;
  return canRecordWeldsAt(auth, own);
}

// MAY THIS PERSON COUNT WHAT'S WAITING HERE (746 D14)? Everyone who may record
// welds here, and every manager at the branch besides: a department manager
// without the switch counts, though they cannot record. count_welded()'s own
// rule, and its row rule weld_count_ok(). An admin's `isManager` is harmless:
// every admin already passes canRecordWelds().
export function canCountWeldedAt(auth, branchId) {
  if (auth?.isPurchasing) return false;
  if (!isAtBranch(auth, branchId)) return false;
  return canRecordWelds(auth) || !!auth?.isManager;
}

// WHY THIS PERSON MAY NOT PRESS HERE - which of the copy table's four reasons a
// size's sheet shows (gate 746, F2: Reason: team member / department manager /
// purchasing / away from the branch), Session 79.22b.6.3.1. Not a new rule:
// null exactly when the rule above it answers true, and otherwise the reason in
// the order the database refuses (assert_may_weld(): purchasing first, then
// anybody not at the branch, then the welding rule). The database still makes
// every refusal; this only chooses the sentence that says why.
// 'team' | 'manager' | 'purchasing' | 'away' | null.
export function weldRefusalKind(auth, branchId) {
  if (canRecordWeldsAt(auth, branchId)) return null;
  if (auth?.isPurchasing) return 'purchasing';
  if (!isAtBranch(auth, branchId)) return 'away';
  return auth?.isManager ? 'manager' : 'team';
}

// The same for the count (Recount welded frames), from canCountWeldedAt(): a
// manager at the branch may count without the switch, so 'manager' never comes
// back here.
export function weldCountRefusalKind(auth, branchId) {
  if (canCountWeldedAt(auth, branchId)) return null;
  if (auth?.isPurchasing) return 'purchasing';
  if (!isAtBranch(auth, branchId)) return 'away';
  return 'team';
}

// An admin or an executive anywhere, or a manager - any manager - whose own
// branch is this one. The shape the next two rules share, as weld_clear_ok() and
// weld_reserve_ok() share one body in the database. Each keeps its own name
// there and here because they answer different decisions (746 D15, D29) and
// either may change without the other: the day one does, it stops calling this,
// and the test's rows for it say so.
function leadsHereOrEverywhere(auth, branchId) {
  const { isAdmin, isExecutive, isManager } = auth || {};
  if (isAdmin || isExecutive) return true;
  return !!isManager && isAtBranch(auth, branchId);
}

// MAY THIS PERSON CLEAR SOMEBODY'S FLAG (746 D15, D22)? Takes the FLAG's
// branch. Never a team member, with the switch or without; never purchasing.
// The database's weld_clear_ok().
export function canClearWeldFlag(auth, flagBranchId) {
  return leadsHereOrEverywhere(auth, flagBranchId);
}

// MAY THIS PERSON CHANGE A RESERVE HERE (746 D29)? Change on a size's sheet,
// Reserves, and Save. Never a team member, with "Can record welds" or without;
// never purchasing. The database's weld_reserve_ok(), which both reserve steps
// and the reserve table's insert rule call.
export function canSetWeldReserve(auth, branchId) {
  return leadsHereOrEverywhere(auth, branchId);
}
